import React, { PropsWithChildren, FC, useCallback, useMemo, useState } from "react";

import { Accordion, Button, FieldCheckbox, LoadingSpinner, URLInput, validateURL } from "@xapp/components";

export interface SocialQuestionStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Updating status
     */
    updating?: boolean;
    /**
     * Status message to display
     */
    updatingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string; message: string };
    /**
     *
     */
    hasWebsite?: boolean;
    /**
     * Initial value to use for the social site
     */
    socialSite?: string;
    /**
     * The user has clicked next
     */
    onNext(hasSocial: boolean, socialSite?: string): void;
    /**
     * Optional, user clicks back button
     */
    onBack?(): void;
}

export const SocialQuestionStep: FC<PropsWithChildren<SocialQuestionStepProps>> = (props) => {
    const { style, className, onNext, updating: analyzing, onBack } = props;

    const [website, setWebsite] = useState<string>(props.socialSite || "");
    const [, setValid] = useState<boolean>(validateURL(props.socialSite).valid);

    const [hasWebsite, setHasWebsite] = useState<boolean>();

    const handleWebsiteChange = useCallback((value: string, valid: boolean) => {
        setWebsite(value);
        setValid(valid);
    }, []);

    const handleNextClick = () => {
        const returnWebsite = hasWebsite ? website : undefined;
        onNext(hasWebsite, returnWebsite);
    };

    const handleHasWebsiteChange = useCallback((value: boolean) => {
        setHasWebsite(value);
    }, []);

    const handleNoWebsiteChange = useCallback((value: boolean) => {
        setHasWebsite(!value);
    }, []);

    const noWebsite: boolean = useMemo(() => {
        return typeof hasWebsite === "boolean" ? !hasWebsite : false;
    }, [hasWebsite]);

    if (analyzing) {
        return (
            <div style={{ ...style }} className={className}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                    }}
                >
                    <LoadingSpinner />
                    <h2>Noting response...</h2>
                </div>
            </div>
        );
    }

    // We just want to make sure that the user has selected one of the options
    const canClickNext: boolean = typeof hasWebsite === "boolean";

    return (
        <>
            <div style={{ ...style }} className={className}>
                <div>
                    <h2>Do you have a business Facebook page?</h2>
                    <p></p>
                    <p>
                        A Facebook page for you business can serve as a business's primary digital store front or just a
                        place to communicate with new and existing customers.
                    </p>
                    <br />
                    <p>
                        If you don't have a website, you can leverage our hosted contact pages for potential customers
                        to book appointments with you.
                    </p>
                    <div style={{ padding: "20px 0px 50px 0px" }}>
                        <Accordion
                            title={
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <FieldCheckbox
                                        fieldStyle={{ marginBottom: "2px" }}
                                        onChange={handleHasWebsiteChange}
                                        value={typeof hasWebsite === "boolean" ? hasWebsite : false}
                                    />
                                    <h3>I have a Facebook page</h3>
                                </div>
                            }
                            onChange={(event, expanded) => {
                                // event.preventDefault();
                                handleHasWebsiteChange(expanded);
                            }}
                            disableExpandIcon={true}
                            expanded={typeof hasWebsite === "boolean" ? hasWebsite : false}
                        >
                            <div>
                                <div style={{ paddingBottom: "20px" }}>
                                    <p>Great! Please copy paste it below...</p>
                                </div>
                                <URLInput
                                    label="Facebook Page"
                                    onChange={handleWebsiteChange}
                                    disabled={typeof hasWebsite === "boolean" ? !hasWebsite : false}
                                    value={website}
                                />
                            </div>
                        </Accordion>
                        <Accordion
                            title={
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <FieldCheckbox
                                        fieldStyle={{ marginBottom: "2px" }}
                                        onChange={handleNoWebsiteChange}
                                        value={noWebsite}
                                    />
                                    <h3>I don't have a Facebook page</h3>
                                </div>
                            }
                            onChange={(event, expanded) => {
                                // event.preventDefault();
                                handleNoWebsiteChange(expanded);
                            }}
                            disableExpandIcon={true}
                            expanded={typeof noWebsite === "boolean" ? noWebsite : false}
                        >
                            <div style={{ paddingBottom: "20px" }}>
                                {/* TODO: Change it so mobile is new tab & desktop is modal */}
                                <p>
                                    No problem! You can always leverage our hosted contact page anywhere you can add a
                                    link.
                                </p>
                            </div>
                        </Accordion>
                    </div>
                    <div
                        style={{
                            padding: "20px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: typeof onBack === "function" ? "space-between" : "right",
                        }}
                    >
                        {typeof onBack === "function" && (
                            <Button leftIcon="arrow_back" label={`Back`} variation="secondary" onClick={onBack} />
                        )}
                        <Button
                            rightIcon="arrow_forward"
                            label={`Next`}
                            variation="primary"
                            onClick={handleNextClick}
                            disabled={!canClickNext}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
